import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    ExportErrorsResponseApiDto,
    AutomationErrorsResponseApiDto
} from '../../dto';
import { HttpParams } from '@angular/common/http';
import {
    AddContactsRequestDto,
    AutomationRequestPayloadDto,
    ContactsResponseDto,
    JobChangeAutomationResponseDto,
    JobChangesResponseDto,
} from 'src/app/modals/job-change-automations/models';
import { UpdatedContactResponse } from 'src/app/modals/job-change-automations/components/updated-contacts/updated-contacts.component';

@Injectable({
    providedIn: 'root'
})
export class JobChangeApiService {
    private readonly url: string = 'service/contactcloud/jobchange';

    constructor(private readonly baseApiService: BaseApiService) {}

    saveJobChangeAutomation(requestPayload: AutomationRequestPayloadDto): Observable<JobChangeAutomationResponseDto> {
        return this.baseApiService.post(`${this.url}/automations`, requestPayload);
    }

    getJobChangeAutomations(): Observable<JobChangeAutomationResponseDto> {
        return this.baseApiService.get<JobChangeAutomationResponseDto>(`${this.url}/automations`);
    }

    updateAutomation(automationId: number, requestPayload: AutomationRequestPayloadDto): Observable<void> {
        return this.baseApiService.patch(`${this.url}/automations`, requestPayload);
    }

    getExportErrors(): Observable<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]> {
        return this.baseApiService.get<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]>(`${this.url}/automations/export/errors`);
    }


    /**
     * Retry failed exports for all automations or a specific automation
     * @param automationId Optional automation ID to retry exports for specific automation
     * @returns Observable with count of retried exports
     */
    retryFailedExports(automationId?: number): Observable<{ count: number }> {
        const requestPayload = automationId ? { automationId } : {};
        return this.baseApiService.post<{ count: number }>(`${this.url}/automations/export/retry`, requestPayload);
    }

    addContactsToAutomation(automationId: number, automationName: string, contacts: { email: string }[]): Observable<JobChangeAutomationResponseDto> {
        // According to API docs, for adding contacts we only need the automation name and contacts list
        const requestPayload: AddContactsRequestDto = {
            automation: { 
                name: automationName
            },
            trackedContacts: contacts.map(contact => ({ email: contact.email }))
        };
        
        // Using spread operator to conditionally add automationId to payload (immutable approach)
        return this.baseApiService.patch<JobChangeAutomationResponseDto>(
            `${this.url}/automations`, 
            {
                ...requestPayload,
                ...(automationId ? { id: automationId } : {})
            }
        );
    }

    getTrackedContacts(
        automationId: number,
        page: number = 1,
        size: number = 100,
        sort: string = 'email',
        order: string = 'ASC'
    ): Observable<ContactsResponseDto> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString())
            .set('sort', sort)
            .set('order', order);

        return this.baseApiService.get<ContactsResponseDto>(`${this.url}/automations/contacts/${automationId}`, { params });
    }

    getTrackedContactsByAutomationName(
        automationName: string,
        page: number = 1,
        size: number = 100,
        sort: string = 'email',
        order: string = 'ASC'
    ): Observable<ContactsResponseDto> {
        const params = new HttpParams()
            .set('automation_name', automationName)
            .set('page', page.toString())
            .set('size', size.toString())
            .set('sort', sort)
            .set('order', order);

        return this.baseApiService.get<ContactsResponseDto>(`${this.url}/automations/contacts`, { params });
    }

    // TODO: Add params type once the API is finalized
    getJobChanges(params?: any): Observable<JobChangesResponseDto> {
        return this.baseApiService.get<JobChangesResponseDto>(`${this.url}/automations/contacts/changes`, { params });
    }

    /**
     * Fetches updated contacts with pagination and filtering
     * @param params Object containing page, size, filter, and automationIds
     * @returns Observable of UpdatedContactResponse
     */
    getUpdatedContacts(params: {
        page: number;
        size: number;
        filter?: string;
        automationIds?: number[];
    }): Observable<UpdatedContactResponse> {
        // Convert automationIds array to comma-separated string if it exists
        let httpParams = new HttpParams()
            .set('page', params.page.toString())
            .set('size', params.size.toString());
        
        if (params.filter && params.filter.trim() !== '') {
            httpParams = httpParams.set('filter', params.filter);
        }
        
        if (params.automationIds && params.automationIds.length > 0) {
            httpParams = httpParams.set('automationIds', params.automationIds.join(','));
        }
        
        return this.baseApiService.get<UpdatedContactResponse>(
            `${this.url}/automations/updated`, 
            { params: httpParams, showError: true }
        );
    }

    /**
     * Downloads updated contacts as CSV with filtering options
     * @param params Object containing optional page, size, filter, automationIds, and matchIds
     * @returns Observable<Blob> of CSV data
     */
    downloadUpdatedContactsCsv(params?: {
        page?: number;
        size?: number;
        filter?: string;
        automationIds?: number[];
        matchIds?: string[];
    }): Observable<Blob> {
        return this.downloadCsv(`${this.url}/automations/updated/csv`, params);
    }

    /**
     * Downloads contacts as CSV with filtering options
     * @param params Object containing optional page, size, filter, automationIds, and matchIds
     * @returns Observable<Blob> of CSV data
     */
    downloadContactsCsv(params?: {
        page?: number;
        size?: number;
        filter?: string;
        automationIds?: number[];
        matchIds?: string[];
    }): Observable<Blob> {
        return this.downloadCsv(`${this.url}/automations/contacts/csv`, params);
    }
    
    /**
     * Generic method to download CSV data from a specified endpoint
     * @param endpoint The API endpoint to call
     * @param params Object containing optional page, size, filter, automationIds, and matchIds
     * @returns Observable<Blob> of CSV data
     * @private
     */
    private downloadCsv(endpoint: string, params?: {
        page?: number;
        size?: number;
        filter?: string;
        automationIds?: number[];
        matchIds?: string[];
    }): Observable<Blob> {
        // Ensure params is defined
        params = params || {};
        
        // Build the query parameters
        let httpParams = new HttpParams();
        
        if (params.page !== undefined) {
            httpParams = httpParams.set('page', params.page.toString());
        }
        
        if (params.size !== undefined) {
            httpParams = httpParams.set('size', params.size.toString());
        }
        
        if (params.filter && params.filter.trim() !== '') {
            httpParams = httpParams.set('filter', params.filter.trim());
        }
        
        if (params.automationIds && params.automationIds.length > 0) {
            httpParams = httpParams.set('automationIds', params.automationIds.join(','));
        }
        
        // Use baseApiService.get with the parameters
        return this.baseApiService.get<Blob>(
            endpoint,
            { params: httpParams },
            { responseType: 'text' }
        ).pipe(
            map(response => new Blob([response as any], { type: 'text/csv' }))
        );
    }
}
